import { z } from 'zod';

export const maxFiles = 6;

export const MAX_FILE_SIZE_MB = 25;

export const requiredMessage = "Поле є обов'язковим";

export const minMessage = 'Мінімальна кількість символів 2';

export const maxDescriptionMessage = 'Максимальна кількість символів 250';

export const validateFileFormat = (file: File | string) => {
  if (typeof file === 'string') return true;

  const allowedFormats = [
    // Excel
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls

    // Word
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx

    // PowerPoint
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx

    // Text & PDF
    'text/plain', // .txt
    'application/pdf', // .pdf

    // Images
    'image/jpeg', // .jpg, .jpeg, .pjpeg, .jfif, .pjp
    'image/png', // .png
    'image/gif', // .gif
    'image/webp', // .webp
    'image/apng', // .apng
    'image/avif', // .avif
    'image/bmp', // .bmp
    'image/x-icon', // .ico
    'image/svg+xml', // .svg, .svgz
    'image/tiff', // .tif, .tiff
    'image/x-xbitmap', // .xbm
  ];

  return allowedFormats.includes(file.type);
};

export const validateFileSize = (file: File | string): boolean => {
  if (file instanceof File) {
    const sizeInMB = file.size / (1024 * 1024);

    return sizeInMB <= MAX_FILE_SIZE_MB;
  }

  return true;
};

export const validation = {
  description: z.string().max(255, { message: maxDescriptionMessage }).optional(),
  documents: z
    .array(
      z.custom<File | string>().refine((file) => validateFileFormat(file), {
        message:
          'Невірний формат файлу. Допустимі формати файлів: .xlsx, .xls, .doc, .docx, .ppt, .pptx, .txt, .pdf, images.',
      }),
    )
    .max(maxFiles),
  name: z
    .string({ message: requiredMessage })
    .min(1, { message: requiredMessage })
    .min(2, { message: minMessage })
    .max(150, { message: 'Максимальна кількість символів 150' })
    .refine(
      (val) => typeof val === 'string' && /^[A-Za-zА-ЩЬЮЯҐЄІЇа-щьюяґєії]+$/.test(val),
      'Неправильно заповнене поле',
    ),
  nameShort: z
    .string({ message: requiredMessage })
    .min(1, { message: requiredMessage })
    .min(2, { message: minMessage })
    .max(25, { message: 'Максимальна кількість символів 25' })
    .refine(
      (val) => typeof val === 'string' && /^[A-Za-zА-ЩЬЮЯҐЄІЇа-щьюяґєії'’]+$/.test(val),
      'Неправильно заповнене поле',
    ),
  newIDCard: z.string().refine((val) => /^\d{9}$/.test(val), 'ID-картка повинна містити 9 цифр'),
  oldPassportNumber: z
    .string()
    .refine((val) => /^\d{6}$/.test(val), 'Номер паспорта повинен містити 6 цифр'),
  // Валідація паспортних даних
  oldPassportSeries: z
    .string()
    .refine((val) => /^[А-ЯІЇЄҐ]{2}$/.test(val), 'Серія повинна складатися з 2 літер кирилицею'),
  optionalName: z
    .string()
    .min(2, { message: minMessage })
    .max(150, { message: 'Максимальна кількість символів 150' })
    .refine((val) => /^[A-Za-zА-ЩЬЮЯҐЄІЇа-щьюяґєії]+$/.test(val), 'Неправильно заповнене поле')
    .optional()
    .nullable(),

  password: z
    .string({ message: requiredMessage })
    .min(8, 'Пароль має містити принаймні 8 символів')
    .max(32, 'Пароль має містити не більше 32 символів')
    .refine(
      // eslint-disable-next-line no-useless-escape
      (val) => /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/.test(val),
      'Пароль може містити тільки латинські літери, спеціальні символи та цифри',
    ),
  phone: z
    .string({ message: requiredMessage })
    .transform((val) => {
      const cleanedValue = val.replace(/[^\d+]/g, '');

      return cleanedValue.startsWith('+') ? cleanedValue : `+${cleanedValue}`;
    })
    .refine((val) => /^\+\d{10,15}$/.test(val), 'Неправильно введений номер телефону'),

  photos: z
    .array(
      z.custom<File | string>().refine(
        (file) => {
          if (file instanceof File) {
            const allowedFormats = ['image/png', 'image/jpeg', 'image/webp'];

            return allowedFormats.includes(file.type);
          }

          return true;
        },
        {
          message: 'Допустимі формати файлів: png, jpg, webp',
        },
      ),
    )
    .max(maxFiles),

  tin: z
    .string({ message: 'ІПН обов’язковий' })
    .refine((val) => /^\d{10}$/.test(val), 'ІПН повинен містити 10 цифр'),

  tinOrEdrpou: z
    .string({ message: 'Номер обов’язковий' })
    .regex(/^(\d{8}|\d{10}|\d{12})$/, 'Номер повинен містити 8, 10 або 12 цифр'),
};
